import { memo } from 'react';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import { getCookie } from 'root/src/client/v2/browser/helpers/cookies';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { useIsBrand } from 'root/src/client/v2/auth/hooks/useIsBrand';
import { useUserData } from 'root/src/client/v2/userData/hooks/useUserData';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from 'root/src/client/v2/common/components/ui/tooltip';
import { buildAssetUrl, assetSize } from 'root/src/client/v2/common/helpers/assetUrl';
import { useCreatorFollow } from 'root/src/client/v2/_pages/creator/redesign/useCreatorFollow';
import { RedesignAddCreatorToProgramModal } from 'root/src/client/v2/_pages/creator/redesign/RedesignAddCreatorToProgramModal';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const SOCIALS = [
    { key: 'twitchData', label: 'Twitch' },
    { key: 'youtubeData', label: 'YouTube' },
    { key: 'tiktokData', label: 'TikTok' },
    { key: 'facebookData', label: 'Facebook' },
];
const compactNumber = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
const formatCount = (value) => compactNumber.format(value !== null && value !== void 0 ? value : 0);
export const CreatorProfileHeader = memo(({ creator, recordId, onFollowChanged }) => {
    var _a;
    const name = creator.displayName || creator.username || 'Creator';
    const accounts = SOCIALS
        .map(({ key, label }) => { var _a, _b, _c; return ({ label, url: (_a = creator[key]) === null || _a === void 0 ? void 0 : _a.url, displayName: (_c = (_b = creator[key]) === null || _b === void 0 ? void 0 : _b.displayName) !== null && _c !== void 0 ? _c : label }); })
        .filter(account => Boolean(account.url));
    const modal = useRedesignModal();
    const { isAdmin } = useIsAdmin();
    const { isBrand } = useIsBrand();
    const isAdminOrBrand = isAdmin || isBrand;
    const isOwnProfile = Boolean(creator.userId) && getCookie('userId') === creator.userId;
    const { userData } = useUserData();
    // getCreatorData only returns ccv to admins, so a creator viewing their own profile falls back to their
    // own ccv from userData (as the legacy profile did). CCV stays private to admins and the profile owner.
    const ccv = typeof creator.ccv === 'number' ? creator.ccv : (isOwnProfile ? (_a = userData === null || userData === void 0 ? void 0 : userData.creatorData) === null || _a === void 0 ? void 0 : _a.ccv : undefined);
    const canSeeCcv = isAdmin || isOwnProfile;
    const { isFollowing, isPending, toggleFollow } = useCreatorFollow({
        recordId,
        following: Boolean(creator.following),
        onChanged: onFollowChanged,
    });
    const canAddToProgram = isAdminOrBrand && Boolean(creator.userId);
    const canFollow = !isOwnProfile && !isAdminOrBrand;
    const openAddToProgram = () => {
        if (!creator.userId) {
            return;
        }
        modal.show(<RedesignAddCreatorToProgramModal userId={creator.userId} displayName={name}/>);
    };
    return (<TooltipProvider delayDuration={150}>
			<div className="glass rounded-2xl border border-solid border-white/[0.08] p-6 sm:p-8">
				<div className="flex flex-col gap-6 sm:flex-row sm:items-start">
					<div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-secondary ring-2 ring-neon-pink/40">
						{creator.image ? (<Img src={buildAssetUrl(creator.image, { w: assetSize('thumbnail'), q: 80, fmt: 'webp' })} alt={name} className="h-full w-full object-cover"/>) : (<div className="flex h-full w-full items-center justify-center font-display text-2xl font-bold text-muted-foreground">
								{name.charAt(0).toUpperCase()}
							</div>)}
					</div>

					<div className="flex-1">
						<div className="flex flex-wrap items-start justify-between gap-3">
							<h1 className="font-display text-3xl font-bold text-gradient-brand">{name}</h1>

							{(canFollow || canAddToProgram) && (<div className="flex shrink-0 items-center gap-2">
									{canAddToProgram && (<Button type="button" variant="secondary" size="sm" onClick={openAddToProgram} className="rounded-xl text-xs font-semibold uppercase tracking-wide">
											<GroupAddOutlinedIcon sx={{ fontSize: 16 }}/>
											<span className="min-w-0">Add to program</span>
										</Button>)}
									{canFollow && (<Tooltip>
											<TooltipTrigger asChild>
												<Button type="button" variant={isFollowing ? 'secondary' : 'brand'} size="sm" aria-label={isFollowing ? 'Unfollow this creator' : 'Follow this creator'} aria-pressed={isFollowing} disabled={isPending} onClick={toggleFollow} className="rounded-xl px-3">
													{isFollowing ? (<FavoriteIcon sx={{ fontSize: 16 }} className="text-neon-pink"/>) : (<FavoriteBorderIcon sx={{ fontSize: 16 }}/>)}
													<span className="min-w-0 text-xs font-semibold uppercase tracking-wide">
														{isFollowing ? 'Following' : 'Follow'}
													</span>
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												{isFollowing ? 'Unfollow this creator' : 'Follow this creator to stay updated'}
											</TooltipContent>
										</Tooltip>)}
								</div>)}
						</div>

						<div className="mt-2 flex flex-wrap items-center gap-5">
							<span className="flex items-center gap-1.5 text-sm text-muted-foreground">
								<GroupsOutlinedIcon sx={{ fontSize: 16 }}/>
								<span className="font-semibold text-neon-cyan">{formatCount(creator.followerCount)}</span> Followers
							</span>
							{canSeeCcv && typeof ccv === 'number' && (<span className="flex items-center gap-1.5 text-sm text-muted-foreground">
									<VisibilityOutlinedIcon sx={{ fontSize: 16 }}/>
									<span className="font-semibold text-neon-cyan">{formatCount(ccv)}</span> CCV
								</span>)}
						</div>

						{accounts.length > 0 && (<div className="mt-5 flex flex-wrap items-center gap-3">
								{accounts.map(account => (<a key={account.label} href={account.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-secondary px-3.5 py-2 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary/80">
										<span className="text-muted-foreground">{account.label}</span>
										{account.displayName}
										<OpenInNewIcon sx={{ fontSize: 12 }} className="text-muted-foreground"/>
									</a>))}
							</div>)}
					</div>
				</div>
			</div>
		</TooltipProvider>);
});
